import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Pencil, Save, X, Sparkles, Loader2, HelpCircle } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { EditableList } from "@/components/EditableList";
import { EditableTable } from "@/components/EditableTable";
import { ProjectionChart, GrowthChart, ScoreBars } from "@/components/StudyCharts";
import { money } from "@/lib/api";

const pct = (a, b) => {
  if (!(Number(a) > 0 && Number(b) > 0)) return "";
  const r = (a / b) * 100;
  return ` (${r < 1 ? r.toPrecision(1) : Math.round(r)}%)`;
};

const MetricHelp = ({ f, view, currency, testId }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" data-testid={`${testId}-help`} className="no-print inline-flex items-center gap-1 rounded-full border border-slate-700 px-2 py-0.5 text-[10px] font-semibold text-slate-400 hover:text-emerald-300 hover:border-emerald-500/50 transition-colors">
        <HelpCircle className="h-3 w-3" />What's this?</button>
    </PopoverTrigger>
    <PopoverContent data-testid={`${testId}-help-popover`} className="w-80 bg-slate-900 border-slate-700 text-slate-200 text-sm space-y-2">
      <p className="font-display font-semibold text-emerald-300">{f.label}: {f.full}</p>
      <p className="text-slate-300">{f.define}</p>
      {view[f.key] != null && <p className="text-slate-400 border-t border-slate-800 pt-2">{f.explain(view, (x) => money(x, currency), pct)}</p>}
    </PopoverContent>
  </Popover>
);

const Metric = ({ f, value, view, editing, onChange, currency, testId }) => (
  <div className={`rounded-lg border border-slate-800 bg-slate-950/50 p-4 ${f.full ? `print-metric-${f.key} border-l-4` : ""}`} style={f.full ? { borderLeftColor: `var(--brand-${f.key})` } : undefined} data-testid={`${testId}-box`}>
    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{f.label}</p>
    {f.full && <div className="mb-2 space-y-1.5">
      <p className="text-xs text-slate-400" data-testid={`${testId}-full`}>{f.full}</p>
      <MetricHelp f={f} view={view} currency={currency} testId={testId} />
      <p className="print-only hidden text-[11px] leading-snug" data-testid={`${testId}-define`}>{f.define}</p>
    </div>}
    {editing ? <Input data-testid={`${testId}-input`} type="number" value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))} className="h-9 bg-slate-950 border-slate-700" />
      : <p data-testid={testId} className="num text-xl font-bold text-slate-100">{f.plain ? value ?? "—" : money(value, currency)}</p>}
  </div>
);

const Field = ({ f, data, editing, set, currency, sectionKey }) => {
  const id = `${sectionKey}-${f.key}`;
  if (f.type === "list") return <EditableList testId={id} items={data[f.key] || []} editing={editing} onChange={(v) => set(f.key, v)} accent={f.accent} />;
  const rows = data[f.key] || [];
  const total = f.total ? rows.reduce((s, r) => s + (Number(r[f.total]) || 0), 0) : null;
  return (
    <div className="space-y-4">
      {f.chart === "projection" && <ProjectionChart data={rows} currency={currency} />}
      {f.chart === "growth" && <GrowthChart data={rows} currency={currency} />}
      {f.chart === "scores" && !editing && <ScoreBars data={rows} />}
      {(f.chart !== "scores" || editing) && <EditableTable testId={id} rows={rows} columns={f.columns} editing={editing} onChange={(v) => set(f.key, v)} currency={currency} />}
      {total !== null && <p className="text-sm text-slate-400 text-right">Total: <span data-testid={`${id}-total`} className="num font-bold text-emerald-400">{money(total, currency)}</span></p>}
    </div>
  );
};

const EMPTY = {};
const clean = (cfg, d) => Object.fromEntries(Object.entries(d).map(([k, v]) => [k, cfg.fields.find((f) => f.key === k)?.type === "list" && Array.isArray(v) ? v.map((x) => x.trim()).filter(Boolean) : v]));

export const SectionCard = ({ cfg, data = EMPTY, currency, onSave, onRegenerate, regenerating, index, children, readOnly }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(data);
  useEffect(() => { if (!editing) setDraft(data); }, [data, editing]);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const view = editing ? draft : data;
  const Icon = cfg.icon;
  const metrics = cfg.fields.filter((f) => f.type === "metric");
  const quads = cfg.fields.filter((f) => f.quad);
  const rest = cfg.fields.filter((f) => f.type !== "metric" && !f.quad);
  const fieldProps = { data: view, editing, set, currency, sectionKey: cfg.key };

  return (
    <section id={cfg.key} data-testid={`study-section-${cfg.key}`} className={`scroll-mt-24 rounded-xl border border-slate-800 bg-slate-900/70 p-6 md:p-8`}>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 grid place-items-center"><Icon className="h-5 w-5 text-emerald-400" /></span>
          <div>
            <p className="num text-xs text-slate-500">{String(index + 1).padStart(2, "0")}</p>
            <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">{cfg.title}</h2>
          </div>
        </div>
        {!readOnly && <div className="no-print flex gap-2">
          {editing ? (
            <>
              <Button size="sm" variant="ghost" data-testid={`${cfg.key}-cancel-btn`} onClick={() => setEditing(false)}><X className="h-4 w-4 mr-1" />Cancel</Button>
              <Button size="sm" data-testid={`${cfg.key}-save-btn`} className="bg-emerald-500 text-emerald-950 hover:bg-emerald-400" onClick={async () => { if (await onSave(cfg.key, clean(cfg, draft))) setEditing(false); }}><Save className="h-4 w-4 mr-1" />Save</Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" data-testid={`${cfg.key}-regenerate-btn`} disabled={regenerating} className="border-slate-700 bg-transparent" onClick={() => onRegenerate(cfg)}>
                {regenerating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1 text-emerald-400" />}AI rewrite
              </Button>
              <Button size="sm" variant="outline" data-testid={`${cfg.key}-edit-btn`} className="border-slate-700 bg-transparent" onClick={() => setEditing(true)}><Pencil className="h-4 w-4 mr-1" />Edit</Button>
            </>
          )}
        </div>}
      </div>

      {children}

      {metrics.length > 0 && <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">{metrics.map((f) => <Metric key={f.key} f={f} value={view[f.key]} view={view} editing={editing} onChange={(v) => set(f.key, v)} currency={currency} testId={`${cfg.key}-${f.key}`} />)}</div>}

      {editing ? (
        <Textarea data-testid={`${cfg.key}-content-input`} rows={10} value={draft.content || ""} onChange={(e) => set("content", e.target.value)} className="bg-slate-950/60 border-slate-700 mb-6 leading-relaxed" />
      ) : (
        <div data-testid={`${cfg.key}-content`} className="space-y-4 mb-6 max-w-3xl">
          {(data.content || "").split(/\n\s*\n/).filter(Boolean).map((p, i) => <p key={i} className="text-slate-300 leading-relaxed">{p}</p>)}
        </div>
      )}

      {quads.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {quads.map((f) => (
            <div key={f.key} className="rounded-lg border border-slate-800 bg-slate-950/40 p-5">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">{f.label}</p>
              <Field f={f} {...fieldProps} />
            </div>
          ))}
        </div>
      )}

      <div className="space-y-6">
        {rest.map((f) => (
          <div key={f.key}>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">{f.label}</p>
            <Field f={f} {...fieldProps} />
          </div>
        ))}
      </div>
    </section>
  );
};
